import { useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import s from "./Testing.module.css";
import { Page } from "../../layout/AppShell";
import { useSession } from "../../app/SessionContext";
import {
  useAddTestAction,
  useAssetTestHistory,
  useTestHistory,
  useTestRecord,
  useTestTypes,
  useUploadTestFile,
} from "../../api/queries";
import { hasPermission, PERMISSION } from "../../api/dataSource";
import {
  ACTION_KINDS,
  ACTION_LABEL,
  describeLog,
  OUTCOME_LABEL,
  proposedThresholds,
  SOURCE_LABEL,
  type ActionKind,
  type Outcome,
  type TestRecordDetail,
  type TestResult,
} from "../../domain/testing";
import { EQUIPMENT_MODULES } from "../../domain/estate";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  numClass,
  Select,
  Skeleton,
  Table,
} from "../../design/components";
import { formatDate, formatDateTime } from "../../lib/format";
import { CreateWorkOrderDialog } from "../work-orders/CreateWorkOrderDialog";
import { DistributionChart } from "./charts";
import { Drawer, Fact, drawerStyles as ds } from "./Drawer";
import { OutcomeText } from "./panels";
import { RecordTestDialog } from "./RecordTestDialog";
import { locale, t } from "../../i18n";
import { saveFile } from "../../lib/saveFile";

const fmt = (v: number | undefined) => (v == null ? "—" : v.toLocaleString(locale(), { maximumFractionDigits: 1 }));
const words = (v: unknown) => String(v ?? "").replace(/[_-]+/g, " ");

/**
 * One test as recorded — interactive. Every figure can be opened to see where
 * it came from; the record can be updated as a new revision; and everything
 * done to it or about it is on a timeline with who and when.
 */
export function TestRecordDetailPage() {
  const { id = "" } = useParams();
  const { principal, source, site } = useSession();
  const navigate = useNavigate();
  const q = useTestRecord(id);
  const history = useTestHistory(id);
  const types = useTestTypes();
  const upload = useUploadTestFile(id);
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [revising, setRevising] = useState(false);
  const [openResult, setOpenResult] = useState<TestResult | null>(null);
  const [openSource, setOpenSource] = useState(false);

  if (q.isPending) return <Skeleton rows={8} height={18} />;
  if (q.isError) return <ErrorState error={q.error} retry={() => void q.refetch()} />;
  const detail = q.data;
  const { record: r, results, files, comparison: cmp } = detail;
  const canUpload = hasPermission(principal, PERMISSION.evidenceWrite);
  const canWrite = hasPermission(principal, PERMISSION.testsWrite);
  const byOutcome = (o: Outcome) => r.counts[o] ?? 0;
  const pct = r.thresholdsUsed.some((th) => th.basis === "pct_of_specified");
  const current = history.data?.revisions.at(-1);
  const superseded = !!r.supersededBy;

  const open = async (objectId: string, name: string) => {
    try {
      const blob = await source.downloadFile(objectId);
      await saveFile(name, blob);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <Page
      eyebrow={t("{site} · testing record · {reference} · revision {n}", {
        site: site.code,
        reference: r.reference,
        n: r.revision,
      })}
      title={r.title}
      actions={
        <div className={`${s.filters} ${s.noPrint}`}>
          {!superseded && (
            <Button
              variant="primary"
              onClick={() => setRevising(true)}
              disabled={!canWrite || !types.data}
              title={
                canWrite
                  ? t("Correct the results or details as the next revision")
                  : t("Requires {permission}", { permission: PERMISSION.testsWrite })
              }
            >
              {t("Update results")}
            </Button>
          )}
          <Button onClick={() => window.print()}>{t("Print")}</Button>
          <Link to="/testing-records">{t("← Testing records")}</Link>
        </div>
      }
    >
      <div className={s.page} data-testid="test-record">
        {superseded && (
          <div className={s.attentionItem} data-severity="attention" role="status">
            <b>{t("This revision has been superseded.")}</b>
            <div className={s.note}>
              {t("It is kept as it was recorded. The current record is")}{" "}
              <Link to={`/testing-records/${r.supersededBy}`}>
                {t("{reference} revision {n}", { reference: r.reference, n: current?.revision ?? r.revision + 1 })}
              </Link>
              .
            </div>
          </div>
        )}
        {r.revision > 1 && !superseded && (
          <div className={s.attentionItem} data-severity="due">
            <b>{t("Revision {n}", { n: r.revision })}</b> — {r.amendmentReason}
            <div className={s.note}>{t("Earlier revisions are kept and listed under Revisions below.")}</div>
          </div>
        )}

        <Card
          title="Verdict"
          sub={
            r.judged
              ? "the engineer's verdict, with its basis"
              : "worked out by the platform from the results and thresholds below"
          }
          actions={
            <Button size="sm" onClick={() => setOpenSource(true)} className={s.noPrint}>
              {t("Data source")}
            </Button>
          }
        >
          <div className={s.panelHead}>
            <span style={{ fontSize: 18 }}>
              <OutcomeText outcome={r.outcome} />
            </span>
            {!r.judged && proposedThresholds(r.thresholdsUsed) && (
              <span className={`${s.badge} ${s.proposed}`}>{t("PROPOSED THRESHOLDS")}</span>
            )}
            {r.demo && <span className={`${s.badge} ${s.demo}`}>{t("DEMONSTRATION DATA")}</span>}
            <button type="button" className={s.linkButton} onClick={() => setOpenSource(true)}>
              {t(SOURCE_LABEL[r.source])}
              {r.sourceFile ? `: ${r.sourceFile}` : ""}
            </button>
          </div>
          <p className={s.note} style={{ fontSize: 14, color: "var(--ink)" }}>
            {r.outcomeBasis}
          </p>
          {results.length > 0 && (
            <div className={s.tiles}>
              {(["fail", "attention", "pass", "not_assessed"] as const).map((o) => (
                <button
                  key={o}
                  type="button"
                  className={`${s.tile} ${s.tileButton}`}
                  onClick={() => {
                    setShowAll(true);
                    document.getElementById(`results-${o}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
                  }}
                  title={t("Show the {outcome} results", { outcome: t(OUTCOME_LABEL[o]).toLowerCase() })}
                >
                  <span className={s.tileLabel}>{t(OUTCOME_LABEL[o])}</span>
                  <span className={`${s.tileValue} ${byOutcome(o) && o !== "pass" ? s[o] : ""}`}>{byOutcome(o)}</span>
                  <span className={s.tileNote}>
                    {cmp
                      ? t("was {n} in {reference}", { n: cmp.prior.counts[o] ?? 0, reference: cmp.prior.reference })
                      : ""}
                  </span>
                </button>
              ))}
            </div>
          )}
          <Table>
            <tbody>
              <tr>
                <th>{t("Test")}</th>
                <td>{r.testTypeName}</td>
              </tr>
              <tr>
                <th>{t("Required by")}</th>
                <td className={s.note}>{r.frameworks.join(" · ")}</td>
              </tr>
              <tr>
                <th>{t("Performed")}</th>
                <td>
                  {formatDate(r.performedOn)}
                  {r.performedBy ? ` ${t("by {name}", { name: r.performedBy })}` : ""}
                </td>
              </tr>
              {r.device && (
                <tr>
                  <th>{t("Device")}</th>
                  <td>{r.device}</td>
                </tr>
              )}
              {r.method && (
                <tr>
                  <th>{t("Method")}</th>
                  <td>{r.method}</td>
                </tr>
              )}
              {r.scope && (
                <tr>
                  <th>{t("Scope")}</th>
                  <td>{r.scope}</td>
                </tr>
              )}
              {Object.entries(r.summary).map(([k, v]) => (
                <tr key={k}>
                  <th>{words(k)}</th>
                  <td className="mono">{String(v)}</td>
                </tr>
              ))}
              <tr>
                <th>{t("Next due")}</th>
                <td>{r.nextDue ? formatDate(r.nextDue) : "—"}</td>
              </tr>
              {r.notes && (
                <tr>
                  <th>{t("Notes")}</th>
                  <td>{r.notes}</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card>

        {cmp && (
          <Card
            title="Against the test before"
            sub={t("{reference} on {date} — {n} results matched by asset or location", {
              reference: cmp.prior.reference,
              date: formatDate(cmp.prior.performedOn),
              n: cmp.matched,
            })}
            actions={
              <Link to={`/testing-records/${cmp.prior.id}`} className={s.noPrint}>
                {t("Open it →")}
              </Link>
            }
          >
            <div className={s.tiles}>
              <div className={s.tile}>
                <span className={s.tileLabel}>{pct ? t("Mean % of specified") : t("Mean")}</span>
                <span className={s.tileValue}>{fmt(cmp.meanLatest)}</span>
                <span className={s.tileNote}>{t("was {value}", { value: fmt(cmp.meanPrior) })}</span>
              </div>
              <div className={s.tile}>
                <span className={s.tileLabel}>{t("Moved down a band")}</span>
                <span className={`${s.tileValue} ${cmp.worsened ? s.attention : ""}`}>{cmp.worsened}</span>
                <span className={s.tileNote}>{t("{n} moved up", { n: cmp.improved })}</span>
              </div>
              <div className={s.tile}>
                <span className={s.tileLabel}>{t("Only in this test")}</span>
                <span className={s.tileValue}>{cmp.onlyLatest}</span>
                <span className={s.tileNote}>{t("{n} only in the earlier one", { n: cmp.onlyPrior })}</span>
              </div>
            </div>
            <DistributionChart
              latest={cmp.rows.map((x) => x.latest)}
              prior={cmp.rows.filter((x) => x.prior != null).map((x) => x.prior!)}
              levels={r.thresholdsUsed.flatMap((th) => [
                ...(th.fail != null
                  ? [{ value: th.fail, label: t("{label} action", { label: th.label }), tone: "fail" as const }]
                  : []),
                ...(th.attention != null
                  ? [
                      {
                        value: th.attention,
                        label: t("{label} attention", { label: th.label }),
                        tone: "attention" as const,
                      },
                    ]
                  : []),
              ])}
              unit={pct ? t("% of specified") : ""}
              latestLabel={r.reference}
              priorLabel={cmp.prior.reference}
            />
          </Card>
        )}

        {results.length > 0 && (
          <Card
            title={t("Results — {n}", { n: results.length })}
            sub="Worst first · click a result for details"
          >
            <div style={{ overflowX: "auto" }}>
              <Table>
                <thead>
                  <tr>
                    <th>{t("Asset / location")}</th>
                    <th style={{ textAlign: "end" }}>{t("Measured")}</th>
                    {pct && <th style={{ textAlign: "end" }}>{t("Specified")}</th>}
                    {pct && <th style={{ textAlign: "end" }}>{t("% of specified")}</th>}
                    <th style={{ textAlign: "end" }}>{t("Before")}</th>
                    <th>{t("Verdict")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(showAll ? results : results.slice(0, 40)).map((x, i, list) => {
                    const before = cmp?.rows.find((c) => c.key === (x.assetTag ?? `@${x.location}`));
                    const firstOfOutcome = list.findIndex((y) => y.outcome === x.outcome) === i;
                    return (
                      <tr
                        key={x.id}
                        id={firstOfOutcome ? `results-${x.outcome}` : undefined}
                        className={s.clickRow}
                        tabIndex={0}
                        onClick={() => setOpenResult(x)}
                        onKeyDown={(e) => e.key === "Enter" && setOpenResult(x)}
                      >
                        <td className="mono">
                          {x.assetTag ?? x.location}
                          {x.assetRemoved && <span className={s.tileNote}> {t("(no longer in the register)")}</span>}
                        </td>
                        <td className={numClass}>{fmt(x.value)}</td>
                        {pct && <td className={numClass}>{fmt(x.specified)}</td>}
                        {pct && <td className={numClass}>{fmt(x.judgedValue)}</td>}
                        <td className={numClass}>{fmt(before?.prior)}</td>
                        <td>
                          <OutcomeText outcome={x.outcome} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
            {results.length > 40 && (
              <Button size="sm" className={s.noPrint} onClick={() => setShowAll((v) => !v)} style={{ marginTop: 8 }}>
                {showAll ? t("Show the worst 40") : t("Show all {n}", { n: results.length })}
              </Button>
            )}
          </Card>
        )}

        <ActionsAndLog
          detail={detail}
          history={history.data}
          historyLoading={history.isPending}
          canWrite={canWrite && !superseded}
        />

        <Card title="Files" sub="Reports and data from every revision">
          {files.length === 0 ? (
            <EmptyState>No file is attached to this record.</EmptyState>
          ) : (
            <Table>
              <tbody>
                {files.map((file, i) => (
                  <tr key={file.id}>
                    <td>
                      <button
                        type="button"
                        className={s.linkButton}
                        onClick={() => void open(file.objectId, file.note ?? `${r.reference}-${i + 1}`)}
                      >
                        {file.note ?? t("{reference} file {n}", { reference: r.reference, n: i + 1 })}
                      </button>
                    </td>
                    <td className={s.tileNote}>{file.contentType ?? t("file")}</td>
                    <td className={numClass}>{file.bytes ? `${Math.max(1, Math.round(file.bytes / 1024))} kB` : ""}</td>
                    <td className={s.tileNote}>{formatDateTime(file.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          {canUpload && (
            <div className={s.noPrint} style={{ marginTop: 10 }}>
              <input
                ref={input}
                type="file"
                multiple
                accept=".pdf,.csv,.xlsx,image/*"
                style={{ display: "none" }}
                onChange={async (e) => {
                  setError(null);
                  for (const file of [...(e.target.files ?? [])]) {
                    try {
                      await upload.mutateAsync({ file, note: file.name });
                    } catch (err) {
                      setError(`${file.name}: ${err instanceof Error ? err.message : String(err)}`);
                      break;
                    }
                  }
                  e.target.value = "";
                }}
              />
              <Button onClick={() => input.current?.click()} disabled={upload.isPending}>
                {upload.isPending ? t("Uploading…") : t("+ Attach files")}
              </Button>
            </div>
          )}
          {error && (
            <div className={s.fail} role="alert" style={{ marginTop: 6 }}>
              {error}
            </div>
          )}
        </Card>
      </div>

      {revising && types.data && (
        <RecordTestDialog
          types={types.data}
          revise={detail}
          onClose={() => setRevising(false)}
          onCreated={(newId) => {
            setRevising(false);
            navigate(`/testing-records/${newId}`);
          }}
        />
      )}
      {openResult && <ResultDrawer detail={detail} result={openResult} onClose={() => setOpenResult(null)} />}
      {openSource && <SourceDrawer detail={detail} onClose={() => setOpenSource(false)} />}
    </Page>
  );
}

/** Where the record's figures came from: how the data arrived, who recorded it, what it was judged against, what it wrote. */
function SourceDrawer({ detail, onClose }: { detail: TestRecordDetail; onClose: () => void }) {
  const { record: r, files, readingsWritten } = detail;
  return (
    <Drawer
      title={t("Data source")}
      sub={t("{reference} · revision {n}", { reference: r.reference, n: r.revision })}
      onClose={onClose}
    >
      <dl className={ds.facts}>
        <Fact
          label={t("Data arrived")}
          source={r.source === "demo" ? t("scripts/seed_demo_tests.py — removable") : undefined}
        >
          {t(SOURCE_LABEL[r.source])}
          {r.sourceFile ? ` — ${r.sourceFile}` : ""}
        </Fact>
        <Fact label={t("Recorded")}>
          {formatDateTime(r.createdAt)}
          {r.createdBy ? ` ${t("by {name}", { name: r.createdBy })}` : ` ${t("(no person — seeded)")}`}
        </Fact>
        <Fact label={t("Tested")}>
          {formatDate(r.performedOn)}
          {r.performedBy ? ` ${t("by {name}", { name: r.performedBy })}` : ""}
        </Fact>
        {r.device && <Fact label={t("Device")}>{r.device}</Fact>}
        {r.method && <Fact label={t("Method")}>{r.method}</Fact>}
        <Fact
          label={t("Verdict")}
          source={
            r.judged
              ? t("the engineer who recorded it")
              : t("the platform, from each result against the thresholds below")
          }
        >
          <OutcomeText outcome={r.outcome} />
        </Fact>
        {r.revision > 1 && (
          <Fact label={t("Revision")}>
            {r.revision} — {r.amendmentReason}
          </Fact>
        )}
        <Fact label={t("Readings written")} source={t("so the rules and prediction models see this test")}>
          {readingsWritten.count ? `${readingsWritten.count} × ${readingsWritten.measurement}` : t("none")}
        </Fact>
        <Fact label={t("Files")}>
          {files.length ? files.map((f) => f.note ?? f.contentType).join(", ") : t("none attached")}
        </Fact>
        <Fact label={t("Required by")}>{r.frameworks.join(" · ")}</Fact>
      </dl>
      {r.thresholdsUsed.length > 0 && (
        <div className={ds.section}>
          <h3>{t("Thresholds, as they stood when this was recorded")}</h3>
          {r.thresholdsUsed.map((th) => (
            <div key={th.label} className={s.note}>
              <b>{th.label}</b>
              {th.match
                ? ` ${t("(where {conditions})", {
                    conditions: Object.entries(th.match)
                      .map(([k, v]) => t("{field} is {value}", { field: words(k), value: v }))
                      .join(", "),
                  })}`
                : ""}
              {t(": attention below {attention}, action below {fail}", {
                attention: th.attention ?? "—",
                fail: th.fail ?? "—",
              })}
              {th.basis === "pct_of_specified" ? ` ${t("% of specified")}` : ""} ·{" "}
              <span className={`${s.badge} ${th.status === "agreed" ? "" : s.proposed}`}>
                {th.status === "agreed" ? t("AGREED") : t("PROPOSED")}
              </span>
              {th.basisNote && <div>{th.basisNote}</div>}
            </div>
          ))}
        </div>
      )}
    </Drawer>
  );
}

/** One result: what was measured, what it was judged against, where it came from, and the same asset in every test of this kind. */
function ResultDrawer({
  detail,
  result: x,
  onClose,
}: {
  detail: TestRecordDetail;
  result: TestResult;
  onClose: () => void;
}) {
  const { record: r, comparison } = detail;
  const history = useAssetTestHistory(r.testType, x.assetTag ?? null);
  const threshold = r.thresholdsUsed.find((th) => th.label === x.thresholdLabel);
  const before = comparison?.rows.find((c) => c.key === (x.assetTag ?? `@${x.location}`));
  const module = EQUIPMENT_MODULES.find((m) => m.tests?.some((mt) => mt.testType === r.testType));
  const pct = threshold?.basis === "pct_of_specified";
  const points = useMemo(() => (history.data ?? []).map((h) => h.judgedValue ?? h.value), [history.data]);
  const max = Math.max(1, ...points, threshold?.attention ?? 0);

  return (
    <Drawer
      title={x.assetTag ?? x.location}
      sub={t("{test} · {reference} rev {n}", { test: r.testTypeName, reference: r.reference, n: r.revision })}
      onClose={onClose}
    >
      <div className={s.panelHead}>
        <span style={{ fontSize: 16 }}>
          <OutcomeText outcome={x.outcome} />
        </span>
        {r.demo && <span className={`${s.badge} ${s.demo}`}>{t("DEMONSTRATION DATA")}</span>}
      </div>
      <dl className={ds.facts}>
        <Fact
          label={t("Measured")}
          source={`${t(SOURCE_LABEL[r.source])}${r.sourceFile ? ` — ${r.sourceFile}` : ""}, ${r.device ?? t("device not recorded")}, ${formatDate(r.performedOn)}`}
        >
          <span className="mono">{fmt(x.value)}</span>
        </Fact>
        {x.specified != null && (
          <Fact label={t("Specified")} source={t("entered with the result")}>
            <span className="mono">{fmt(x.specified)}</span> → <b>{fmt(x.judgedValue)}%</b> {t("of specified")}
          </Fact>
        )}
        <Fact
          label={t("Judged against")}
          source={
            threshold
              ? `${threshold.status === "agreed" ? t("agreed threshold") : t("proposed threshold")}${threshold.basisNote ? ` — ${threshold.basisNote}` : ""}`
              : undefined
          }
        >
          {threshold
            ? `${threshold.label}${t(": attention below {attention}, action below {fail}", { attention: threshold.attention ?? "—", fail: threshold.fail ?? "—" })}${pct ? " %" : ""}`
            : t("No threshold matched this result, so it was not assessed.")}
        </Fact>
        {Object.keys(x.attrs).length > 0 && (
          <Fact label={t("Attributes")} source={t("the asset register, as it was when recorded")}>
            {Object.entries(x.attrs)
              .map(([k, v]) => `${words(k)}: ${words(v)}`)
              .join(" · ")}
          </Fact>
        )}
        <Fact label={t("Test before")}>
          {before?.prior != null ? (
            <>
              {t("{value} in {reference} ({outcome}) · change", {
                value: fmt(before.prior),
                reference: comparison!.prior.reference,
                outcome: before.priorOutcome ? t(OUTCOME_LABEL[before.priorOutcome]) : "—",
              })}{" "}
              <b>
                {before.delta! > 0 ? "+" : ""}
                {fmt(before.delta)}
              </b>
            </>
          ) : (
            t("not in the earlier test")
          )}
        </Fact>
      </dl>

      {x.assetTag && (
        <div className={ds.section}>
          <h3>{t("{asset} in every {test} test", { asset: x.assetTag, test: r.testTypeName.toLowerCase() })}</h3>
          {history.isPending ? (
            <Skeleton rows={2} />
          ) : (history.data ?? []).length === 0 ? (
            <div className={s.note}>{t("No other result for this asset.")}</div>
          ) : (
            <>
              <svg viewBox="0 0 300 80" className={s.spark} role="img" aria-label={t("This asset across tests")}>
                {threshold?.fail != null && (
                  <line
                    x1="0"
                    x2="300"
                    y1={70 - (60 * threshold.fail) / max}
                    y2={70 - (60 * threshold.fail) / max}
                    className={s.levelFail}
                  />
                )}
                {threshold?.attention != null && (
                  <line
                    x1="0"
                    x2="300"
                    y1={70 - (60 * threshold.attention) / max}
                    y2={70 - (60 * threshold.attention) / max}
                    className={s.levelAttention}
                  />
                )}
                {points.length > 1 && (
                  <polyline
                    points={points
                      .map((v, i) => `${10 + (280 * i) / (points.length - 1)},${70 - (60 * v) / max}`)
                      .join(" ")}
                    className={s.lineTrend}
                  />
                )}
                {points.map((v, i) => (
                  <circle
                    key={i}
                    cx={points.length > 1 ? 10 + (280 * i) / (points.length - 1) : 150}
                    cy={70 - (60 * v) / max}
                    r={4}
                    className={s.dotTrend}
                  />
                ))}
              </svg>
              <Table>
                <tbody>
                  {(history.data ?? []).map((h) => (
                    <tr key={h.recordId}>
                      <td>
                        <Link to={`/testing-records/${h.recordId}`} onClick={onClose}>
                          {h.reference}
                        </Link>
                        {h.revision > 1 ? ` ${t("rev {n}", { n: h.revision })}` : ""}
                      </td>
                      <td>{formatDate(h.performedOn)}</td>
                      <td className={numClass}>
                        {fmt(h.judgedValue ?? h.value)}
                        {h.specified != null ? "%" : ""}
                      </td>
                      <td>
                        <OutcomeText outcome={h.outcome} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
          {module && (
            <Link to={module.route} onClick={onClose}>
              {t("Open {title} →", { title: t(module.title) })}
            </Link>
          )}
        </div>
      )}
    </Drawer>
  );
}

/**
 * What was done about the test, and every change to the record: one timeline.
 * Actions are added here — or a work order raised, which is logged as one —
 * and can never be edited or removed.
 */
function ActionsAndLog({
  detail,
  history,
  historyLoading,
  canWrite,
}: {
  detail: TestRecordDetail;
  history: ReturnType<typeof useTestHistory>["data"];
  historyLoading: boolean;
  canWrite: boolean;
}) {
  const { record: r, results } = detail;
  const add = useAddTestAction(r.id);
  const [kind, setKind] = useState<Exclude<ActionKind, "work_order_raised">>("action_taken");
  const [body, setBody] = useState("");
  const [tags, setTags] = useState("");
  const [raising, setRaising] = useState(false);
  const [filter, setFilter] = useState<"all" | "actions" | "changes">("all");
  const failing = results.filter((x) => x.outcome === "fail" || x.outcome === "attention");

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!body.trim()) return;
    add.mutate(
      { kind, body: body.trim(), assetTags: tags.split(/[,\s]+/).filter(Boolean) },
      {
        onSuccess: () => {
          setBody("");
          setTags("");
        },
      },
    );
  };

  const log = (history?.log ?? []).filter((e) =>
    filter === "all"
      ? true
      : filter === "actions"
        ? /action_taken|comment|work_order_raised|retest_planned/.test(e.action)
        : !/action_taken|comment|work_order_raised|retest_planned/.test(e.action),
  );
  const workOrders = new Map((history?.actions ?? []).filter((a) => a.workOrderId).map((a) => [a.workOrderId!, a]));

  return (
    <Card
      title="Actions & activity"
      sub="Actions on this test and every change, with who and when"
    >
      {canWrite && (
        <form className={s.actionForm} onSubmit={submit} aria-label={t("Log an action")}>
          <div className={s.filters}>
            <Select
              value={kind}
              onChange={(e) => setKind(e.target.value as typeof kind)}
              aria-label={t("Kind of entry")}
            >
              {ACTION_KINDS.filter((k) => k !== "work_order_raised").map((k) => (
                <option key={k} value={k}>
                  {t(ACTION_LABEL[k])}
                </option>
              ))}
            </Select>
            <Input
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder={t("Asset tags (optional)")}
              aria-label={t("Asset tags")}
              style={{ width: 220 }}
            />
            <Button
              onClick={() => setRaising(true)}
              disabled={failing.length === 0 && !r.judged}
              title={t("New Work Order from this test; it is logged here")}
            >
              {t("New Work Order")}
            </Button>
          </div>
          <textarea
            className={s.textarea}
            rows={2}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={
              kind === "action_taken"
                ? t("e.g. Lamps replaced on C5 — AGL-CL-0032, 0033")
                : kind === "retest_planned"
                  ? t("e.g. Re-survey C5 after relamping, week 40")
                  : t("Comment")
            }
            aria-label={t("What was done")}
          />
          <div className={s.filters}>
            {add.isError && (
              <span className={s.fail}>{t("Not logged: {message}", { message: (add.error as Error).message })}</span>
            )}
            <Button
              type="submit"
              variant="primary"
              disabled={!body.trim() || add.isPending}
              style={{ marginInlineStart: "auto" }}
            >
              {add.isPending ? t("Logging…") : t("Log {kind}", { kind: t(ACTION_LABEL[kind]).toLowerCase() })}
            </Button>
          </div>
        </form>
      )}

      <div className={s.filters} style={{ margin: "10px 0" }}>
        {(["all", "actions", "changes"] as const).map((f) => (
          <Button key={f} size="sm" variant={filter === f ? "primary" : "ghost"} onClick={() => setFilter(f)}>
            {f === "all" ? t("Everything") : f === "actions" ? t("Actions taken") : t("Changes to the record")}
          </Button>
        ))}
      </div>

      {historyLoading ? (
        <Skeleton rows={3} />
      ) : log.length === 0 ? (
        <EmptyState>Nothing logged yet.</EmptyState>
      ) : (
        <ol className={s.timeline}>
          {log.map((e) => {
            const wo = e.after?.work_order_id ? workOrders.get(String(e.after.work_order_id)) : undefined;
            return (
              <li key={e.id} className={s.timelineItem} data-action={e.action.split(".").pop()}>
                <div className={s.timelineHead}>
                  <b>{e.label}</b>
                  <span className={s.tileNote}>
                    {e.by ?? t("no person (seeded)")} · {formatDateTime(e.at)}
                  </span>
                </div>
                <div className={s.note}>{describeLog(e)}</div>
                {wo?.workOrderId && (
                  <Link to={`/work-orders/${wo.workOrderId}`}>
                    {wo.workOrderNumber} · {words(wo.workOrderStatus)} →
                  </Link>
                )}
                {Array.isArray(e.after?.asset_tags) && (e.after!.asset_tags as string[]).length > 0 && (
                  <div className={s.tileNote}>
                    {t("Assets:")} {(e.after!.asset_tags as string[]).join(", ")}
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}

      {history && history.revisions.length > 1 && (
        <>
          <h3 style={{ fontSize: 14, margin: "14px 0 6px" }}>{t("Revisions")}</h3>
          <Table>
            <tbody>
              {history.revisions.map((v) => (
                <tr key={v.id}>
                  <td>
                    {v.id === r.id ? (
                      <b>{t("Revision {n} (this)", { n: v.revision })}</b>
                    ) : (
                      <Link to={`/testing-records/${v.id}`}>{t("Revision {n}", { n: v.revision })}</Link>
                    )}
                  </td>
                  <td>
                    <OutcomeText outcome={v.outcome} />
                  </td>
                  <td className={s.tileNote}>
                    {t(SOURCE_LABEL[v.source])}
                    {v.sourceFile ? ` — ${v.sourceFile}` : ""}
                  </td>
                  <td className={s.tileNote}>{v.amendmentReason ?? t("original")}</td>
                  <td className={s.tileNote}>
                    {formatDateTime(v.createdAt)}
                    {v.createdBy ? ` · ${v.createdBy}` : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}

      {raising && (
        <RaiseFromTest
          detail={detail}
          failing={failing}
          onClose={() => setRaising(false)}
          onRaised={(wo) => {
            setRaising(false);
            add.mutate({
              kind: "work_order_raised",
              body: `${wo.number} raised: ${wo.title}`,
              workOrderId: wo.id,
              assetTags: failing
                .map((x) => x.assetTag)
                .filter((tag): tag is string => !!tag)
                .slice(0, 50),
            });
          }}
        />
      )}
    </Card>
  );
}

function RaiseFromTest({
  detail,
  failing,
  onClose,
  onRaised,
}: {
  detail: TestRecordDetail;
  failing: TestResult[];
  onClose: () => void;
  onRaised: (wo: { id: string; number: string; title: string }) => void;
}) {
  const r = detail.record;
  const worst = failing.filter((x) => x.outcome === "fail");
  const list = (worst.length ? worst : failing).slice(0, 25);
  return (
    <CreateWorkOrderDialog
      onClose={onClose}
      onCreated={(wo) => onRaised({ id: wo.id, number: wo.number, title: wo.title })}
      prefill={{
        title: `${r.testTypeName}: ${worst.length ? `${worst.length} below the action level` : r.judged ? "act on test finding" : `${failing.length} need attention`} (${r.reference})`,
        kind: "corrective",
        priority: worst.length ? 2 : 3,
        assetTag: list.length === 1 ? list[0].assetTag : undefined,
        description: [
          `From ${r.reference} rev ${r.revision}, ${r.performedOn}${r.performedBy ? `, ${r.performedBy}` : ""}.`,
          r.outcomeBasis,
          list.length
            ? `Results: ${list.map((x) => `${x.assetTag ?? x.location} ${fmt(x.judgedValue ?? x.value)}${x.specified != null ? "% of specified" : ""}`).join("; ")}`
            : "",
        ]
          .filter(Boolean)
          .join("\n"),
      }}
    />
  );
}
